<template>
  <div class="new-fundraiser-page">
    <div class="sub-header">Fundraiser Details</div>
    <div class="overflow-y-scroll scrollbar gap-1 flex flex-col">
      <AutoComplete
        class="w-full max-w-md mb-2"
        id="search_organization"
        data-test="search-organization"
        :suggestions="filteredOrganizations"
        v-model="fundraiserStore.selectedOrganization"
        :disabled="fundraiserStore.benefittingOrg?.disabledOrg || fundraiserStore.editFundraiser"
        placeholder="Search Organizations"
        dropdown
        :forceSelection="true"
        @complete="searchOrganizations"
        @change="onOrganizationChange"
      />
      <small
        v-if="fundraiserStore.errors.benefittingOrg"
        class="p-error mb-2"
      >
        {{ fundraiserStore.errors.benefittingOrg }}
      </small>

      <InputText
        class="w-full max-w-md"
        v-model="fundraiserStore.fundraiserTitle"
        id="fundraiser-title"
        data-test="fundraiser-title"
        :class="{ 'p-invalid': fundraiserStore.errors.fundraiserTitle }"
        placeholder="Fundraiser Title*"
      />
      <small
        v-if="fundraiserStore.errors.fundraiserTitle"
        class="p-error mb-2"
      >
        {{ fundraiserStore.errors.fundraiserTitle }}
      </small>
      <small
        v-else
        class="mb-2"
      >
        This name will be publicly displayed
      </small>

      <InputText
        class="w-full max-w-md"
        v-model="donorStore.donorFullName"
        id="creator-name"
        data-test="creator-name"
        :disabled="true"
        placeholder="Creator Name*"
      />
      <small class="mb-2"> This name will be publicly displayed </small>

      <InputText
        class="w-full max-w-md"
        v-model="fundraiserStore.urlLink"
        id="custom-url"
        data-test="custom-url"
        :disabled="fundraiserStore.editFundraiser"
        :class="{ 'p-invalid': fundraiserStore.errors.urlLink }"
        placeholder="Custom URL*"
      />
      <small
        v-if="fundraiserStore.errors.urlLink"
        class="p-error mb-2"
      >
        {{ fundraiserStore.errors.urlLink }}
      </small>
      <small
        v-else
        class="mb-2"
      >
        {{ customUrlInfoText }}
      </small>

      <InputNumber
        class="w-full max-w-md"
        v-model="fundraiserStore.goal"
        id="fundraiser-goal"
        data-test="fundraiser-goal"
        :class="{ 'p-invalid': fundraiserStore.errors.goal }"
        placeholder="Fundraiser Goal*"
        mode="currency"
        currency="USD"
      />
      <small
        v-if="fundraiserStore.errors.goal"
        class="p-error"
      >
        {{ fundraiserStore.errors.goal }}
      </small>

      <LabeledField
        class="max-w-md"
        label="I'm an employee of:"
        labelClass="!text-base poppins"
        :field="fundraiserStore.companyGiving"
      >
        <AutoComplete
          v-model="fundraiserStore.companyGiving"
          forceSelection
          optionLabel="name"
          fluid
          :suggestions="filteredCompanies"
          :disabled="fundraiserStore.editFundraiser"
          @complete="searchCompanies"
        />

        <div class="flex text-gray-500 mt-1 text-xs">
          <i class="fas fa-info-circle ml-1 mr-3 mt-[2px]"></i>
          <div v-if="!fundraiserStore.companyGiving">Optional</div>
          <div v-else>Your employer will see this fundraiser and it will be displayed on the Company Giving page.</div>
        </div>
      </LabeledField>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useCompanyAdminStore } from '@/stores/CompanyAdminStore';
import { useFundraiserStore } from '@/stores/FundraiserStore.js';
import { useDonorStore } from '@/stores/DonorStore.js';
import LabeledField from '@/components/forms/LabeledField.vue';

const companyAdminStore = useCompanyAdminStore();
const fundraiserStore = useFundraiserStore();
const donorStore = useDonorStore();

const filteredCompanies = ref([]);
const filteredOrganizations = ref([]);

const searchOrganizations = (event) => {
  const query = event.query?.toLowerCase() || '';
  if (query) {
    filteredOrganizations.value = fundraiserStore.organizations
      .filter((org) => org.organizationName.toLowerCase().includes(query))
      .map((org) => org.organizationName);
  } else {
    filteredOrganizations.value = fundraiserStore.organizations.map((org) => org.organizationName);
  }
};

// Handle the selected organization
const onOrganizationChange = (e) => {
  const selectedName = e.value; // This is the organizationName
  const selectedOrg = fundraiserStore.organizations.find((org) => org.organizationName === selectedName);

  if (selectedOrg) {
    fundraiserStore.benefittingOrgHostsId = selectedOrg.organizations_hosts_id;
    delete fundraiserStore.errors.benefittingOrg;
  } else {
    fundraiserStore.benefittingOrgHostsId = null;
  }
};

const customUrlInfoText = computed(() => {
  if (fundraiserStore.urlLink && !fundraiserStore.errors.urlLink) {
    return `Your fundraiser page will be visible at ${donorStore.hostUrl}/${fundraiserStore.urlLink}`;
  }
  return `Custom URL will be used to access your fundraiser page. It may contain only letters and hyphens.`;
});

const searchCompanies = async (event) => {
  const query = event.query;
  if (query.length > 0) {
    const companies = await companyAdminStore.filteredCompanies(query);
    filteredCompanies.value = companies;
  } else {
    const companies = fundraiserStore.companies.map((company) => company.name);
    filteredCompanies.value = companies;
  }
};
</script>

<style lang="scss" scoped>
.sub-header {
  font-size: 1.625rem;
  font-weight: 500;
  margin-bottom: 16px;
}

.inputs w-full max-w-md {
  width: 430px;
}

@media screen and (max-width: 768px) {
  .sub-header {
    text-align: center;
  }
  .inputs w-full max-w-md {
    width: 100%;
  }
}
</style>
